import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChildren, useZones, isActiveChild } from "@/hooks/useChildren";
import { useStaff } from "@/hooks/useStaff";
import { useShifts } from "@/hooks/useShifts";
import { useAttendance } from "@/hooks/useAttendance";
import { useNurseryId } from "@/hooks/useNurseryId";
import { createUniqueRealtimeTopic, isRealtimeCallbackOrderError, SAFE_REALTIME_CHANNEL_OPTIONS } from "@/lib/realtimeChannels";
import {
  computeZoneCompliance,
  filterActiveShifts,
  type ZoneCompliance,
} from "@/lib/ratioEngine";

function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Live ratio compliance for every active zone, recomputed every 30s
 * and on any realtime change to attendance / shifts.
 */
export function useRatioCompliance(): {
  zones: ZoneCompliance[];
  isLoading: boolean;
} {
  const qc = useQueryClient();
  const nurseryId = useNurseryId();
  const today = useMemo(() => new Date(), []);
  const todayStr = fmtDate(today);

  const { data: zones, isLoading: lz } = useZones();
  const { data: children, isLoading: lc } = useChildren();
  const { data: staff, isLoading: ls } = useStaff();
  const { data: shifts, isLoading: lsh } = useShifts(todayStr, todayStr);
  const { data: attendance, isLoading: la } = useAttendance(todayStr);

  // Setup guardrail (matches the server check): no ratio alerts until this
  // nursery has ever created a staff profile. Archived profiles still count.
  const { data: everStaff } = useQuery({
    queryKey: ["staff-ever-count", nurseryId],
    enabled: !!nurseryId,
    staleTime: 60_000,
    queryFn: async () => {
      const { count } = await supabase
        .from("staff")
        .select("id", { count: "exact", head: true })
        .eq("nursery_id", nurseryId!);
      return count ?? 0;
    },
  });

  // Tick every 30s to refresh "now" relative ratios.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Realtime invalidation
  useEffect(() => {
    if (!nurseryId) return;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    try {
      channel = supabase
        .channel(createUniqueRealtimeTopic("ratio", nurseryId), SAFE_REALTIME_CHANNEL_OPTIONS)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "attendance", filter: `nursery_id=eq.${nurseryId}` },
          () => {
            qc.invalidateQueries({ queryKey: ["attendance", nurseryId, todayStr] });
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "shifts", filter: `nursery_id=eq.${nurseryId}` },
          () => {
            qc.invalidateQueries({ queryKey: ["shifts", nurseryId, todayStr, todayStr] });
          },
        )
        .subscribe((status, err) => {
          if (status === "CHANNEL_ERROR") {
            console.warn("[ratio-realtime] Subscription error; live ratios will continue polling.", err);
          }
        });
    } catch (error) {
      const message = isRealtimeCallbackOrderError(error)
        ? "Realtime channel collision prevented; live ratios will continue polling."
        : "Realtime setup failed; live ratios will continue polling.";
      console.warn(`[ratio-realtime] ${message}`, error);
      if (channel) supabase.removeChannel(channel);
      channel = null;
    }
    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [nurseryId, qc, todayStr]);

  const result = useMemo<ZoneCompliance[]>(() => {
    if (!zones || !children || !staff || !shifts || !attendance) return [];
    if (everStaff === 0) return [];
    const now = new Date();
    const active = filterActiveShifts(shifts as any, now);
    const unassigned = new Set(
      (active as any[])
        .filter((s) => !s.zone_id && s.actual_start_time && !s.actual_end_time)
        .map((s) => s.staff_id),
    ).size;
    const signedInIds = new Set(
      attendance
        .filter((a) => a.status === "present" && a.check_in_time && !a.check_out_time)
        .map((a) => a.child_id),
    );
    return zones.map((z) => {
      const signed = children.filter(
        (c) => c.zone_id === z.id && isActiveChild(c) && signedInIds.has(c.id),
      );
      return computeZoneCompliance({
        zone: z as any,
        signedInChildren: signed,
        activeShifts: active as any,
        staffList: staff as any,
        now,
        unassignedStaffSignedIn: unassigned,
      });
    });
  }, [zones, children, staff, shifts, attendance, everStaff]);

  return {
    zones: result,
    isLoading: lz || lc || ls || lsh || la,
  };
}
